import React, { useEffect, useRef, useState } from "react";
import { Modal } from "bootstrap/dist/js/bootstrap.bundle.min.js";
import { Icon } from "@iconify/react/dist/iconify.js";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "../i18n/I18nContext";
import { listActiveStudents } from "../services/student";
import { getNotificationHistory } from "../services/notification";
import NotifyModal from "./NotifyModal";

const MODAL_ID = "notifyAllActiveModal";
const MAPUTO_TZ = "Africa/Maputo";

const toISODate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const QUICK_FILTERS = ["today", "week", "month"];

// "week" começa na Segunda-feira (convenção local), "month" vai do dia 1 até hoje.
const rangeForQuickFilter = (filter) => {
  const now = new Date();
  if (filter === "today") {
    const iso = toISODate(now);
    return { from: iso, to: iso };
  }
  if (filter === "week") {
    const day = now.getDay();
    const diffToMonday = day === 0 ? 6 : day - 1;
    const monday = new Date(now);
    monday.setDate(now.getDate() - diffToMonday);
    return { from: toISODate(monday), to: toISODate(now) };
  }
  const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  return { from: toISODate(firstOfMonth), to: toISODate(now) };
};

// O backend (Lambda) corre em UTC. Os campos novos (sentAt/deliveredAt/readAt)
// já vêm em ISO com "Z" (UTC inequívoco), mas o "createdAt" legado é gravado
// como "YYYY-MM-DD HH:mm:ss" em UTC sem qualquer marcador — se o deixarmos
// assim, o browser interpreta-o como hora local dele e o valor aparece 2h
// atrasado para quem está em Moçambique. Por isso assumimos UTC explicitamente
// quando a string não traz "Z"/offset, e formatamos sempre para Africa/Maputo
// (UTC+2, sem horário de Verão) independentemente do fuso do browser do utilizador.
const parseServerDate = (value) => {
  if (!value) return null;
  let raw = String(value).trim();
  if (!/[zZ]|[+-]\d{2}:?\d{2}$/.test(raw)) {
    raw = `${raw.replace(" ", "T")}Z`;
  }
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDateTime = (value) => {
  const date = parseServerDate(value);
  if (!date) return "-";
  return date.toLocaleString("pt-PT", {
    timeZone: MAPUTO_TZ,
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatTime = (value) => {
  const date = parseServerDate(value);
  if (!date) return "-";
  return date.toLocaleTimeString("pt-PT", { timeZone: MAPUTO_TZ, hour: "2-digit", minute: "2-digit" });
};

// O histórico (tabela SGE_SMS) tem shapes diferentes consoante o fluxo que
// gravou o registo (WhatsApp/Meta grava "status: 'sent'|'delivered'|'read'|'failed'"
// via routes/api/whatsapp.js, o fluxo SMS/msg91 legado não grava "status" nenhum)
// — ver src/services/notification.js.
const STATUS_META = {
  sent: { label: "notificacoes.status.sent", cls: "bg-neutral-200 text-neutral-600" },
  delivered: { label: "notificacoes.status.delivered", cls: "bg-info-focus text-info-main" },
  read: { label: "notificacoes.status.read", cls: "bg-success-focus text-success-main" },
  failed: { label: "notificacoes.status.failed", cls: "bg-danger-focus text-danger-main" },
};

const StatusBadge = ({ status, t }) => {
  const meta = STATUS_META[status] || STATUS_META.sent;
  return (
    <span className={`px-12 py-4 radius-8 fw-medium text-sm ${meta.cls}`}>
      {t(meta.label)}
    </span>
  );
};

const NotificacoesLayer = () => {
  const { t } = useTranslation();
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [pairs, setPairs] = useState([]);

  const [quickFilter, setQuickFilter] = useState("month");
  const [range, setRange] = useState(() => rangeForQuickFilter("month"));

  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const modalRef = useRef(null);

  const fetchHistory = (currentRange = range) => {
    setLoadingHistory(true);
    getNotificationHistory(undefined, currentRange)
      .then((resp) => {
        const rows = Array.isArray(resp.data) ? resp.data : [];
        rows.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
        setHistory(rows);
      })
      .catch(() => setHistory([]))
      .finally(() => setLoadingHistory(false));
  };

  useEffect(() => {
    fetchHistory(range);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.from, range.to]);

  const applyQuickFilter = (filter) => {
    setQuickFilter(filter);
    setRange(rangeForQuickFilter(filter));
  };

  const setCustomFrom = (e) => {
    setQuickFilter("custom");
    setRange((r) => ({ ...r, from: e.target.value }));
  };

  const setCustomTo = (e) => {
    setQuickFilter("custom");
    setRange((r) => ({ ...r, to: e.target.value }));
  };

  const toggleExpand = (id) => setExpandedId((prev) => (prev === id ? null : id));

  const openNotifyAllActive = async () => {
    setLoadingRecipients(true);
    try {
      const resp = await listActiveStudents();
      const registrations = resp.data || [];
      const idContactPairs = registrations
        .map((rec) => ({ registrationId: rec.id, contact: rec.student?.carier?.contact }))
        .filter((pair) => pair.contact);

      if (idContactPairs.length === 0) {
        toast.error(t("notificacoes.error.noRecipients"));
        return;
      }

      setPairs(idContactPairs);
      Modal.getOrCreateInstance(modalRef.current).show();
    } catch (err) {
      toast.error(t("notificacoes.send.error"));
    } finally {
      setLoadingRecipients(false);
    }
  };

  return (
    <div className='row gy-4'>
      <ToastContainer />

      <div className='col-12'>
        <div className='card radius-12'>
          <div className='card-body'>
            <h6 className='mb-4'>{t("notificacoes.send.title")}</h6>
            <p className='text-secondary-light mb-24 text-sm'>{t("notificacoes.send.subtitle")}</p>

            <div className='row gy-3 align-items-end'>
              <div className='col-12'>
                <button
                  type='button'
                  className='btn btn-primary-600 d-flex align-items-center gap-2'
                  onClick={openNotifyAllActive}
                  disabled={loadingRecipients}
                >
                  <Icon icon='mage:message-notify' />
                  {loadingRecipients ? "..." : t("notificacoes.send.action")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='col-12'>
        <div className='card radius-12'>
          <div className='card-body'>
            <div className='d-flex flex-wrap align-items-center justify-content-between gap-3 mb-24'>
              <div>
                <h6 className='mb-4'>{t("notificacoes.history.title")}</h6>
                <p className='text-secondary-light mb-0 text-sm'>{t("notificacoes.history.subtitle")}</p>
              </div>
              <button
                type='button'
                className='btn btn-outline-secondary d-flex align-items-center gap-2'
                onClick={() => fetchHistory()}
                disabled={loadingHistory}
              >
                <Icon icon='ic:round-refresh' />
                {t("global.refresh")}
              </button>
            </div>

            <div className='d-flex flex-wrap align-items-end gap-3 mb-24'>
              <div className='d-flex gap-2'>
                {QUICK_FILTERS.map((filter) => (
                  <button
                    key={filter}
                    type='button'
                    className={`btn btn-sm radius-8 ${
                      quickFilter === filter ? "btn-primary-600" : "btn-outline-secondary"
                    }`}
                    onClick={() => applyQuickFilter(filter)}
                  >
                    {t(`notificacoes.filter.${filter}`)}
                  </button>
                ))}
              </div>

              <div className='d-flex align-items-end gap-2'>
                <div>
                  <label className='form-label text-sm mb-8'>{t("notificacoes.filter.from")}</label>
                  <input
                    type='date'
                    className='form-control form-control-sm'
                    value={range.from}
                    max={range.to}
                    onChange={setCustomFrom}
                  />
                </div>
                <div>
                  <label className='form-label text-sm mb-8'>{t("notificacoes.filter.to")}</label>
                  <input
                    type='date'
                    className='form-control form-control-sm'
                    value={range.to}
                    min={range.from}
                    onChange={setCustomTo}
                  />
                </div>
              </div>
            </div>

            <div className='table-responsive scroll-sm'>
              <table className='table bordered-table mb-0'>
                <thead>
                  <tr>
                    <th scope='col' style={{ width: 40 }} />
                    <th scope='col'>{t("global.name")}</th>
                    <th scope='col'>{t("global.contact")}</th>
                    <th scope='col'>{t("notificacoes.history.message")}</th>
                    <th scope='col'>{t("notificacoes.history.status")}</th>
                    <th scope='col'>{t("notificacoes.history.sentAt")}</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingHistory && (
                    <tr>
                      <td colSpan={6} className='text-center text-secondary-light py-32'>
                        {t("estudante.list.loading")}
                      </td>
                    </tr>
                  )}
                  {!loadingHistory && history.length === 0 && (
                    <tr>
                      <td colSpan={6} className='text-center text-secondary-light py-32'>
                        {t("notificacoes.history.empty")}
                      </td>
                    </tr>
                  )}
                  {!loadingHistory &&
                    history.map((row, idx) => {
                      const rowId = row.id || `${row.number}-${idx}`;
                      const isExpanded = expandedId === rowId;
                      return (
                        <React.Fragment key={rowId}>
                          <tr
                            role='button'
                            onClick={() => toggleExpand(rowId)}
                            className={isExpanded ? "bg-neutral-50" : ""}
                          >
                            <td>
                              <Icon
                                icon={isExpanded ? "ic:round-expand-less" : "ic:round-expand-more"}
                                className='text-secondary-light text-xl'
                              />
                            </td>
                            <td className='fw-semibold text-primary-light'>{row.studentName || "-"}</td>
                            <td>{row.number || "-"}</td>
                            <td className='text-truncate' style={{ maxWidth: 360 }}>
                              {row.message || "-"}
                            </td>
                            <td>
                              <StatusBadge status={row.status} t={t} />
                            </td>
                            <td>{formatDateTime(row.createdAt || row.sentAt)}</td>
                          </tr>
                          {isExpanded && (
                            <tr>
                              <td colSpan={6} className='p-0'>
                                <div className='p-24 bg-neutral-25 border-top'>
                                  <div className='row gy-16'>
                                    <div className='col-md-8'>
                                      <span className='text-secondary-light text-sm fw-medium d-block mb-8'>
                                        {t("notificacoes.details.message")}
                                      </span>
                                      <p className='mb-16 text-break'>{row.message || "-"}</p>

                                      <span className='text-secondary-light text-sm fw-medium d-block mb-8'>
                                        {t("notificacoes.details.attachment")}
                                      </span>
                                      {row.attachmentUrl ? (
                                        <a
                                          href={row.attachmentUrl}
                                          target='_blank'
                                          rel='noreferrer'
                                          className='d-inline-flex align-items-center gap-2 text-primary-600 fw-medium'
                                        >
                                          <Icon icon='solar:file-text-outline' />
                                          {row.attachmentName || t("notificacoes.details.viewAttachment")}
                                        </a>
                                      ) : (
                                        <span className='text-secondary-light'>{t("notificacoes.details.noAttachment")}</span>
                                      )}
                                    </div>
                                    <div className='col-md-4'>
                                      <ul className='list-unstyled d-flex flex-column gap-12 mb-0'>
                                        <li className='d-flex align-items-center justify-content-between gap-3'>
                                          <span className='text-secondary-light text-sm'>
                                            <Icon icon='solar:check-circle-outline' className='me-1' />
                                            {t("notificacoes.details.sentAt")}
                                          </span>
                                          <span className='fw-medium text-sm'>
                                            {formatTime(row.sentAt || row.createdAt)}
                                          </span>
                                        </li>
                                        <li className='d-flex align-items-center justify-content-between gap-3'>
                                          <span className='text-secondary-light text-sm'>
                                            <Icon icon='solar:check-read-outline' className='me-1' />
                                            {t("notificacoes.details.deliveredAt")}
                                          </span>
                                          <span className='fw-medium text-sm'>
                                            {row.deliveredAt ? formatTime(row.deliveredAt) : "-"}
                                          </span>
                                        </li>
                                        <li className='d-flex align-items-center justify-content-between gap-3'>
                                          <span className='text-secondary-light text-sm'>
                                            <Icon icon='solar:eye-outline' className='me-1' />
                                            {t("notificacoes.details.readAt")}
                                          </span>
                                          <span className='fw-medium text-sm'>
                                            {row.readAt ? formatTime(row.readAt) : "-"}
                                          </span>
                                        </li>
                                        {row.status === "failed" && row.errorMessage && (
                                          <li className='text-danger-main text-sm'>{row.errorMessage}</li>
                                        )}
                                      </ul>
                                    </div>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <NotifyModal
        modalId={MODAL_ID}
        pairs={pairs}
        count={pairs.length}
        onSent={() => fetchHistory()}
        modalRefOverride={modalRef}
      />
    </div>
  );
};

export default NotificacoesLayer;
